import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, orderBy, query, doc, deleteDoc } from "firebase/firestore";
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { StyledTableCell, StyledTableRow } from '../../utils/styled'
import { formatDate } from "../../utils/dateUtils";

const firestore = getFirestore();
const currentUser = JSON.parse(localStorage.getItem("currentUser"));
const isAdmin = currentUser?.rol === 'admin';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function RemitoHerramientasList() {
    const [remitos, setRemitos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [remitoToDelete, setRemitoToDelete] = useState(null);
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });

    // Search and Pagination
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        getRemitos();
    }, []);

    const getRemitos = async () => {
        setLoading(true);
        try {
            const q = query(collection(firestore, "remitos_herramientas"), orderBy("id", "desc"));
            const querySnapshot = await getDocs(q);
            const remitosData = [];
            querySnapshot.forEach((doc) => {
                remitosData.push({ ...doc.data(), docId: doc.id });
            });
            setRemitos(remitosData);
        } catch (error) {
            console.error("Error al obtener remitos de herramientas:", error);
            setSnackbarInfo({ text: 'Error al cargar remitos', type: 'error', open: true });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDelete = () => {
        setOpenDeleteModal(false);
        setRemitoToDelete(null);
    };

    const deleteRemito = async () => {
        try {
            const docRef = doc(firestore, 'remitos_herramientas', String(remitoToDelete));
            await deleteDoc(docRef);
            setSnackbarInfo({ text: 'Remito eliminado correctamente!', type: 'success', open: true });
            setOpenDeleteModal(false);
            getRemitos();
        } catch (error) {
            console.error('Error al eliminar remito:', error);
            setOpenDeleteModal(false);
            setSnackbarInfo({ text: 'Ocurrió un error al eliminar', type: 'error', open: true });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarInfo({ text: '', type: 'info', open: false });
    };

    // Filter remitos based on search input (crew name/number or id)
    const filteredRemitos = remitos.filter(r => {
        const query = searchQuery.toLowerCase();
        return (
            String(r.id).includes(query) ||
            (r.cuadrillaEntrega && String(r.cuadrillaEntrega).toLowerCase().includes(query)) ||
            (r.cuadrillaReceptora && String(r.cuadrillaReceptora).toLowerCase().includes(query)) ||
            (r.tipoRemito && String(r.tipoRemito).toLowerCase().includes(query)) ||
            (r.fecha && formatDate(r.fecha).includes(query))
        );
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedRemitos = filteredRemitos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Container component="main" maxWidth="xl" sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Listado de Remitos de Herramientas</Typography>
                <Button variant="contained" href="/herramientas-remito">Nuevo Remito</Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                    label="Buscar por ID, Cuadrilla, Tipo o Fecha"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    fullWidth
                />
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>ID Remito</StyledTableCell>
                                    <StyledTableCell>Fecha</StyledTableCell>
                                    <StyledTableCell>Tipo</StyledTableCell>
                                    <StyledTableCell>De la cuadrilla (Origen)</StyledTableCell>
                                    <StyledTableCell>A la cuadrilla (Destino)</StyledTableCell>
                                    <StyledTableCell>Observaciones</StyledTableCell>
                                    <StyledTableCell align="center">Acciones</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRemitos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            No se encontraron remitos
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedRemitos.map((row) => (
                                        <StyledTableRow key={row.id}>
                                            <StyledTableCell>{row.id}</StyledTableCell>
                                            <StyledTableCell>{row.fecha ? formatDate(row.fecha) : ''}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    label={row.tipoRemito}
                                                    color={row.tipoRemito === 'ENTREGA' ? 'primary' : 'secondary'}
                                                    size="small"
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>{row.cuadrillaEntrega || 'Depósito cba'}</StyledTableCell>
                                            <StyledTableCell>{row.cuadrillaReceptora || 'Depósito cba'}</StyledTableCell>
                                            <StyledTableCell>{row.observaciones}</StyledTableCell>
                                            <StyledTableCell align="center">
                                                <IconButton
                                                    aria-label="print"
                                                    href={`/imprimir-herramienta/${row.id}`}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <PrintIcon />
                                                </IconButton>
                                                {isAdmin && (
                                                    <IconButton
                                                        aria-label="delete"
                                                        onClick={() => {
                                                            setOpenDeleteModal(true);
                                                            setRemitoToDelete(row.id);
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ color: 'red' }} />
                                                    </IconButton>
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={filteredRemitos.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Registros por página:"
                    />
                </Paper>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteModal} onClose={handleCloseDelete}>
                <DialogTitle>Eliminar Remito de Herramientas</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro que desea eliminar el remito #{remitoToDelete}? Esta acción es permanente y no revertirá automáticamente el stock de las cuadrillas.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>Cancelar</Button>
                    <Button onClick={deleteRemito} color="error" autoFocus>Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarInfo.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default RemitoHerramientasList;
